// =============================================================================
// src/features/explore/explore-markers.ts — Construcao dos marcadores de POI/acoes.
//
// Proveniencia: assets/explore.js `buildMarkers` (233-265) e `jitter` (266-270).
//   Extraido do controller como builders PUROS de L.layerGroup, recebendo os
//   centroides e o resolvedor de nome — mantem o arquivo do mapa < 500 linhas.
//   O jitter (espiral de Vogel, angulo aureo 2.39996) e a estrutura HTML dos
//   marcadores sao paridade visual — NAO alterar.
//
// Leaflet: L.marker/L.divIcon/L.layerGroup verificados via Context7 (/leaflet/leaflet).
// =============================================================================

import L from 'leaflet';
import type { LatLng, LayerGroup as LeafletLayerGroup, Marker } from 'leaflet';
import type { MunicipalityId, ExplorePoiCategory, ExploreActionType, ExploreCampaignMarkerKind } from '../../app/types';
import { EXPLORE_DATASET, campaignProfileOf } from '../../data';
import { CAT_META, ACAO_META, CAMPAIGN_MARKER_META } from './constants';

/** Mapa id -> centroide do municipio (preenchido pelo controller). */
export type MunCentroidMap = ReadonlyMap<MunicipalityId, LatLng>;
/** Resolve o nome de exibicao de um municipio. */
export type MunNameResolver = (id: MunicipalityId) => string;

/**
 * Espiral deterministica de Vogel p/ espalhar marcadores no mesmo municipio
 * (assets/explore.js 266-270). Angulo aureo 2.39996; NAO alterar (paridade).
 */
function jitterOffset(index: number): readonly [dx: number, dy: number] {
  const angle = index * 2.39996;
  const radius = 0.04 + 0.025 * index;
  return [Math.cos(angle) * radius, Math.sin(angle) * radius];
}

/** Centro deslocado pelo jitter da i-esima ocorrencia no municipio. */
function jitteredLatLng(center: LatLng, index: number): [number, number] {
  const [dx, dy] = jitterOffset(index);
  return [center.lat + dy, center.lng + dx];
}

/** Marcador de um POI (icone Tabler colorido) com tooltip nome·municipio. */
function buildPoiMarker(cat: ExplorePoiCategory, latlng: [number, number], name: string, munName: string): Marker {
  const meta = CAT_META[cat];
  const html = `<span class="exp-marker" style="--mc:${meta.color}"><i class="ti ${meta.icon}"></i></span>`;
  const marker = L.marker(latlng, { icon: L.divIcon({ className: 'exp-marker-wrap', html, iconSize: [26, 26], iconAnchor: [13, 13] }) });
  marker.bindTooltip(`${name} · ${munName}`, { direction: 'top', offset: [0, -11], className: 'exp-tip' });
  return marker;
}

/** Marcador de uma acao de campo (icone + data) com tooltip tipo·titulo·data. */
function buildAcaoMarker(type: ExploreActionType, latlng: [number, number], title: string, date: string): Marker {
  const meta = ACAO_META[type] ?? ACAO_META.reuniao;
  const html = `<span class="exp-marker exp-marker--acao"><i class="ti ${meta.icon}"></i><span class="exp-marker-date">${date}</span></span>`;
  const marker = L.marker(latlng, { icon: L.divIcon({ className: 'exp-marker-wrap', html, iconSize: [30, 30], iconAnchor: [15, 15] }) });
  marker.bindTooltip(`${meta.label} · ${title} · ${date}`, { direction: 'top', offset: [0, -14], className: 'exp-tip' });
  return marker;
}

/** Monta um grupo de camada por categoria de POI (assets/explore.js 234-249). */
export function buildPoiGroups(centroids: MunCentroidMap, munName: MunNameResolver): Map<ExplorePoiCategory, LeafletLayerGroup> {
  const groups = new Map<ExplorePoiCategory, LeafletLayerGroup>();
  (Object.keys(CAT_META) as ExplorePoiCategory[]).forEach((cat) => groups.set(cat, L.layerGroup()));
  const counts = new Map<MunicipalityId, number>();
  EXPLORE_DATASET.pois.forEach((poi) => {
    const center = centroids.get(poi.mun);
    if (!center) return;
    const index = (counts.get(poi.mun) ?? 0) + 1;
    counts.set(poi.mun, index);
    groups.get(poi.cat)?.addLayer(buildPoiMarker(poi.cat, jitteredLatLng(center, index), poi.name, munName(poi.mun)));
  });
  return groups;
}

/** Marcador de campanha (lideranca/doador): icone Tabler colorido + tooltip. */
function buildCampaignMarker(kind: ExploreCampaignMarkerKind, latlng: [number, number], munName: string): Marker {
  const meta = CAMPAIGN_MARKER_META[kind];
  const html = `<span class="exp-marker" style="--mc:${meta.color}"><i class="ti ${meta.icon}"></i></span>`;
  const marker = L.marker(latlng, { icon: L.divIcon({ className: 'exp-marker-wrap', html, iconSize: [26, 26], iconAnchor: [13, 13] }) });
  marker.bindTooltip(`${meta.label} · ${munName}`, { direction: 'top', offset: [0, -11], className: 'exp-tip' });
  return marker;
}

/** Acrescenta `count` marcadores de um tipo no municipio, espalhados pelo jitter. */
function addCampaignMarkers(group: LeafletLayerGroup, kind: ExploreCampaignMarkerKind, center: LatLng, count: number, munName: string, seed: number): void {
  for (let i = 0; i < count; i++) {
    group.addLayer(buildCampaignMarker(kind, jitteredLatLng(center, i + seed), munName));
  }
}

/**
 * Monta os grupos de marcadores de campanha (lideranca/doador) a partir do perfil
 * derivado de cada municipio. diverge do golden por decisao de produto 2026-05-31.
 */
export function buildCampaignGroups(centroids: MunCentroidMap, munName: MunNameResolver): Map<ExploreCampaignMarkerKind, LeafletLayerGroup> {
  const groups = new Map<ExploreCampaignMarkerKind, LeafletLayerGroup>([
    ['lideranca', L.layerGroup()],
    ['doador', L.layerGroup()],
  ]);
  centroids.forEach((center, id) => {
    const profile = campaignProfileOf(id);
    addCampaignMarkers(groups.get('lideranca') as LeafletLayerGroup, 'lideranca', center, profile.liderancas, munName(id), 1);
    addCampaignMarkers(groups.get('doador') as LeafletLayerGroup, 'doador', center, profile.doadores, munName(id), 5);
  });
  return groups;
}

/** Monta o grupo de camada das acoes de campo (assets/explore.js 250-264). */
export function buildAcoesGroup(centroids: MunCentroidMap): LeafletLayerGroup {
  const group = L.layerGroup();
  const counts = new Map<MunicipalityId, number>();
  EXPLORE_DATASET.acoes.forEach((acao) => {
    const center = centroids.get(acao.mun);
    if (!center) return;
    const index = (counts.get(acao.mun) ?? 0) + 1;
    counts.set(acao.mun, index);
    group.addLayer(buildAcaoMarker(acao.type, jitteredLatLng(center, index + 4), acao.title, acao.date));
  });
  return group;
}
